import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, CornerDownLeft, Search, X } from "lucide-react";

const commands = [
  { label: "Explore the skill map", href: "#skill-map", hint: "Interactive demo" },
  { label: "See how it works", href: "#how-it-works", hint: "Three steps" },
  { label: "Review future benchmarks", href: "#benchmarks", hint: "Outcome data" },
  { label: "Read common questions", href: "#faq", hint: "Career transitions" },
  { label: "Join early access", href: "#early-access", hint: "Waitlist" },
];

export function CommandPalette() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = useMemo(
    () => commands.filter((command) => command.label.toLowerCase().includes(query.toLowerCase())),
    [query],
  );

  function open() {
    dialogRef.current?.showModal();
    setQuery("");
    setActiveIndex(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function close() {
    dialogRef.current?.close();
  }

  function runCommand(index: number) {
    const command = filtered[index];
    if (!command) return;
    close();
    document.querySelector(command.href)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    const onShortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        dialogRef.current?.open ? close() : open();
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, []);

  function handleKeys(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % Math.max(filtered.length, 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + Math.max(filtered.length, 1)) % Math.max(filtered.length, 1));
    }
    if (event.key === "Enter") {
      event.preventDefault();
      runCommand(activeIndex);
    }
  }

  return (
    <>
      <button className="search-pill" type="button" onClick={open} aria-label="Open page navigator">
        <Search aria-hidden="true" size={16} />
        <span className="search-pill__label">Navigate</span>
        <kbd>⌘K</kbd>
      </button>

      <dialog
        ref={dialogRef}
        className="command-dialog"
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
        onClose={() => setQuery("")}
      >
        <div className="command-dialog__field">
          <Search aria-hidden="true" size={20} />
          <label className="sr-only" htmlFor="command-search">Search page sections</label>
          <input
            ref={inputRef}
            id="command-search"
            type="search"
            placeholder="Where do you want to go?"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeys}
          />
          <button className="icon-button" type="button" onClick={close} aria-label="Close page navigator">
            <X aria-hidden="true" size={18} />
          </button>
        </div>
        <div className="command-dialog__results" role="listbox" aria-label="Page destinations">
          {filtered.length ? (
            filtered.map((command, index) => (
              <button
                key={command.href}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? "is-active" : ""}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => runCommand(index)}
              >
                <span>{command.label}</span>
                <small>{command.hint}</small>
              </button>
            ))
          ) : (
            <p className="command-dialog__empty">No section matches that search.</p>
          )}
        </div>
        <footer className="command-dialog__footer" aria-hidden="true">
          <span><ArrowUp size={14} /><ArrowDown size={14} /> navigate</span>
          <span><CornerDownLeft size={14} /> open</span>
          <span>esc close</span>
        </footer>
      </dialog>
    </>
  );
}
